/**
 * Pause, Resume, End for cause and Release hold (TMMPDM-0042). Each confirms. End and Release
 * need a reason of 3–500 characters. A 409 `AFFILIATE_STATE` is the server's status change:
 * the dialog closes and the caller re-reads. Nothing here decides the affiliate's status.
 */
import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, isPayoutsMovedToLedger } from '@/api/errors';
import { MovedToLedgerMessage } from '../ledger/ledgerShared';
import type { AffiliateDetail } from '@/api/tmm/types';

const REASON_MIN = 3;
const REASON_MAX = 500;

function isAffiliateState(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 409 && String(error.code) === 'AFFILIATE_STATE'
  );
}

interface NamedAction {
  open: boolean;
  slug: string;
  affiliateId: number;
  name: string;
  onClose: () => void;
  /** The write's answer. */
  onDone: (detail: AffiliateDetail) => void;
  /** 409: the status moved. The caller re-reads. */
  onStateChanged: () => void;
}

export function PauseAffiliateDialog({
  open,
  slug,
  affiliateId,
  name,
  onClose,
  onDone,
  onStateChanged,
}: NamedAction) {
  const { t } = useCopy();
  const [busy, setBusy] = useState(false);
  // A failed pause is said inside its confirm, which stays open (TMM-B4).
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await adminApi.pauseAffiliate(slug, affiliateId));
    } catch (failure: unknown) {
      if (isAffiliateState(failure)) onStateChanged();
      else setError(t('affiliates.pause.failed', 'Could not pause this affiliate.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      open={open}
      title={t('affiliates.pause.title', 'Pause {{name}}?', { name })}
      consequence={t(
        'affiliates.pause.words',
        'New referrals stop being attributed; existing ones keep earning.',
      )}
      confirmLabel={t('affiliates.pause.confirm', 'Pause')}
      busy={busy}
      error={error}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      onConfirm={() => void confirm()}
    />
  );
}

export function ResumeAffiliateDialog({
  open,
  slug,
  affiliateId,
  name,
  onClose,
  onDone,
  onStateChanged,
}: NamedAction) {
  const { t } = useCopy();
  const [busy, setBusy] = useState(false);
  // A failed resume is said inside its confirm, which stays open (TMM-B4).
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await adminApi.resumeAffiliate(slug, affiliateId));
    } catch (failure: unknown) {
      if (isAffiliateState(failure)) onStateChanged();
      else setError(t('affiliates.resume.failed', 'Could not resume this affiliate.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog
      open={open}
      title={t('affiliates.resume.title', 'Resume {{name}}?', { name })}
      consequence={t('affiliates.resume.words', 'New referrals can be attributed again.')}
      confirmLabel={t('affiliates.resume.confirm', 'Resume')}
      busy={busy}
      error={error}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      onConfirm={() => void confirm()}
    />
  );
}

interface ReasonDialogProps {
  open: boolean;
  title: string;
  words: string;
  confirmLabel: string;
  failed: string;
  destructive?: boolean;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
  /** TMMPDM-0095: a 410 `PAYOUTS_MOVED_TO_LEDGER` is said here with this Ledger link, then re-read. */
  ledgerTo?: string | undefined;
  onMovedToLedger?: (() => void) | undefined;
}

function ReasonDialog({
  open,
  title,
  words,
  confirmLabel,
  failed,
  destructive = false,
  busy,
  onCancel,
  onSubmit,
  ledgerTo,
  onMovedToLedger,
}: ReasonDialogProps) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="sm">
      {open ? (
        <ReasonForm
          title={title}
          words={words}
          confirmLabel={confirmLabel}
          failed={failed}
          destructive={destructive}
          busy={busy}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ledgerTo={ledgerTo}
          onMovedToLedger={onMovedToLedger}
        />
      ) : null}
    </Dialog>
  );
}

function ReasonForm({
  title,
  words,
  confirmLabel,
  failed,
  destructive,
  busy,
  onCancel,
  onSubmit,
  ledgerTo,
  onMovedToLedger,
}: Omit<ReasonDialogProps, 'open'>) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<ReactNode>(null);
  const [submitting, setSubmitting] = useState(false);
  const working = busy || submitting;

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < REASON_MIN || trimmed.length > REASON_MAX) {
      setFieldError(t('affiliates.reason.length', 'Use 3 to 500 characters.'));
      return;
    }
    setFieldError(null);
    setFormError(null);
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } catch (error: unknown) {
      if (isAffiliateState(error)) return;
      if (isPayoutsMovedToLedger(error)) {
        setFormError(<MovedToLedgerMessage ledgerTo={ledgerTo} />);
        onMovedToLedger?.();
        return;
      }
      setFormError(failed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{words}</DialogContentText>
        {formError ? (
          <DialogContentText color="error" sx={{ mb: 1 }}>
            {formError}
          </DialogContentText>
        ) : null}
        <TextField
          fullWidth
          required
          label={t('affiliates.reason.label', 'Reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={fieldError !== null}
          helperText={fieldError ?? ' '}
          slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={working}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton
          busy={working}
          color={destructive ? 'error' : 'primary'}
          onClick={() => void submit()}
        >
          {confirmLabel}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

export function EndForCauseDialog({
  open,
  slug,
  affiliateId,
  name,
  onClose,
  onDone,
  onStateChanged,
}: NamedAction) {
  const { t } = useCopy();
  return (
    <ReasonDialog
      open={open}
      title={t('affiliates.end.title', 'End {{name}} for cause', { name })}
      words={t(
        'affiliates.end.words',
        "Ending here stops all earning and holds unpaid commission for review. It can't be undone.",
      )}
      confirmLabel={t('affiliates.end.confirm', 'End for cause')}
      failed={t('affiliates.end.failed', 'Could not end this affiliate.')}
      destructive
      busy={false}
      onCancel={onClose}
      onSubmit={async (reason) => {
        try {
          onDone(await adminApi.endAffiliate(slug, affiliateId, { reason }));
        } catch (error: unknown) {
          if (isAffiliateState(error)) {
            onStateChanged();
            return;
          }
          throw error;
        }
      }}
    />
  );
}

interface ReleaseProps {
  open: boolean;
  slug: string;
  affiliateId: number;
  name: string;
  onClose: () => void;
  onReleased: () => void;
  /** TMMPDM-0095: the affiliate's Ledger, linked from a 410 `PAYOUTS_MOVED_TO_LEDGER`. */
  ledgerTo?: string | undefined;
  /** A 410: the caller re-reads (its actions hide once the mode is `LIVE`). */
  onMovedToLedger?: (() => void) | undefined;
}

/** Release hold, from the affiliate page or a held statement row. */
export function ReleaseHoldDialog({
  open,
  slug,
  affiliateId,
  name,
  onClose,
  onReleased,
  ledgerTo,
  onMovedToLedger,
}: ReleaseProps) {
  const { t } = useCopy();
  return (
    <ReasonDialog
      open={open}
      title={t('affiliates.release.title', 'Release hold')}
      words={t('affiliates.release.words', "Release {{name}}'s unpaid commission for payout?", {
        name,
      })}
      confirmLabel={t('affiliates.release.confirm', 'Release hold')}
      failed={t('affiliates.release.failed', 'Could not release the hold.')}
      busy={false}
      ledgerTo={ledgerTo}
      onMovedToLedger={onMovedToLedger}
      onCancel={onClose}
      onSubmit={async (reason) => {
        await adminApi.releaseAffiliateHold(slug, affiliateId, { reason });
        onReleased();
      }}
    />
  );
}
